/**
 * ContextualHelpIcon Component (AC-656)
 *
 * A contextual help icon that, when clicked, opens the Help Center
 * to a specific documentation topic.
 *
 * Technical Design Specification:
 * - Renders QuestionMarkCircleIcon from @heroicons/react/24/outline
 * - On click, calls helpStore.openHelpToTopic(docPath)
 * - Shows tooltip on hover (default: "Learn more")
 * - Supports size variants: 'sm' | 'md' | 'lg' (default: 'sm')
 * - Has aria-label for accessibility
 * - Keyboard accessible (Enter/Space activates)
 *
 * @see AC-656 - Create ContextualHelpIcon component
 * @see AC-648 - Epic: Contextual Help System
 */

import React, { useState } from 'react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useHelpStore } from '../../store/helpStore';

export interface ContextualHelpIconProps {
  /** Path to documentation (e.g., "features/agents.md") */
  docPath: string;
  /** Optional tooltip text (defaults to "Learn more") */
  tooltip?: string;
  /** Icon size (default: 'sm') */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Size class mappings for the icon.
 */
const sizeClasses: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

/**
 * ContextualHelpIcon - A help icon that opens the Help Center to a specific topic.
 *
 * @param docPath - Path to documentation (e.g., "features/agents.md")
 * @param tooltip - Optional tooltip text (defaults to "Learn more")
 * @param size - Icon size: 'sm' | 'md' | 'lg' (default: 'sm')
 * @param className - Additional CSS classes
 */
const ContextualHelpIcon: React.FC<ContextualHelpIconProps> = ({
  docPath,
  tooltip = 'Learn more',
  size = 'sm',
  className = '',
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { openHelpToTopic } = useHelpStore();

  /**
   * Handle click to open help to the specified topic.
   */
  const handleClick = () => {
    openHelpToTopic(docPath);
  };

  /**
   * Handle keyboard events for accessibility.
   * Activates on Enter or Space key press.
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openHelpToTopic(docPath);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
      aria-label="Get help"
      className={`relative inline-flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${className}`}
    >
      <QuestionMarkCircleIcon className={sizeClasses[size]} />
      {showTooltip && (
        <span
          role="tooltip"
          className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg whitespace-nowrap z-[100]"
        >
          {tooltip}
        </span>
      )}
    </button>
  );
};

export default ContextualHelpIcon;
